import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchFamilyStory, fetchChildren, familyStoryPdfUrl, shareFamilyStory } from "@/lib/api";
import { BookOpen, Loader2, ChevronLeft, ChevronRight, Sparkles, Download, Share2, X } from "lucide-react";
import { toast } from "sonner";
import PageErrorBoundary from "@/components/PageErrorBoundary";
import FeedbackBar from "@/components/FeedbackBar";
import AnsweredByPill from "@/components/AnsweredByPill";

function StoryInner() {
  const today = new Date();
  const [params, setParams] = useSearchParams();
  const initY = parseInt(params.get("y") || "", 10);
  const initM = parseInt(params.get("m") || "", 10);
  const [year, setYear] = useState(Number.isFinite(initY) && initY > 2000 ? initY : today.getFullYear());
  const [month, setMonth] = useState(Number.isFinite(initM) && initM >= 1 && initM <= 12 ? initM : today.getMonth() + 1);
  const [childId, setChildId] = useState("");
  const [children, setChildren] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    fetchChildren()
      .then((rows) => setChildren((rows || []).filter((c) => !c.is_demo)))
      .catch(() => setChildren([]));
    const cidParam = params.get("child_id");
    if (cidParam) setChildId(cidParam);
  }, []); // eslint-disable-line

  useEffect(() => {
    setLoading(true);
    fetchFamilyStory(year, month, childId || undefined)
      .then(setData).catch(() => setData(null))
      .finally(() => setLoading(false));
    const np = new URLSearchParams(params);
    np.set("y", String(year));
    np.set("m", String(month));
    if (childId) np.set("child_id", childId);
    else np.delete("child_id");
    setParams(np, { replace: true });
  }, [year, month, childId]); // eslint-disable-line

  const prev = () => { if (month === 1) { setYear(year - 1); setMonth(12); } else setMonth(month - 1); };
  const next = () => { if (month === 12) { setYear(year + 1); setMonth(1); } else setMonth(month + 1); };

  return (
    <div data-testid="family-story-page" className="space-y-6">
      <header className="fcc-card p-6 sm:p-8">
        <p className="label-overline flex items-center gap-2"><BookOpen className="w-3 h-3" /> Family Story</p>
        <h1 className="font-display text-3xl text-[var(--fcc-ink)] mt-2">Your family month, retold</h1>
        <p className="text-sm text-[var(--fcc-ink-soft)] mt-2 max-w-2xl">
          A short, warm narrative of what happened in your family — auto-generated from your milestones, emails, tasks, and events.
        </p>
        <div className="mt-5 flex flex-wrap gap-2 items-center">
          <button data-testid="story-prev" onClick={prev} className="p-2 rounded-lg border border-[var(--fcc-border)]"><ChevronLeft className="w-4 h-4" /></button>
          <span data-testid="story-month-label" className="font-display text-lg text-[var(--fcc-ink)] px-3">
            {new Date(year, month - 1, 1).toLocaleString("en-US", { month: "long", year: "numeric" })}
          </span>
          <button data-testid="story-next" onClick={next} className="p-2 rounded-lg border border-[var(--fcc-border)]"><ChevronRight className="w-4 h-4" /></button>
          {children.length > 0 && (
            <select
              data-testid="story-child-select"
              value={childId}
              onChange={(e) => setChildId(e.target.value)}
              className="fcc-input text-sm py-2 px-3 ml-2"
            >
              <option value="">All family</option>
              {children.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <div className="ml-auto flex flex-wrap gap-2">
            <a
              data-testid="story-download-pdf"
              href={familyStoryPdfUrl(year, month, childId || undefined)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[var(--fcc-border)] bg-white text-sm font-semibold text-[var(--fcc-ink)] hover:bg-[var(--fcc-cream-2)]"
            >
              <Download className="w-4 h-4" /> Download PDF
            </a>
            <button
              data-testid="story-share-open"
              onClick={() => setShareOpen(true)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--fcc-green)] text-white text-sm font-semibold hover:bg-[var(--fcc-green-dark)] transition"
            >
              <Share2 className="w-4 h-4" /> Share
            </button>
          </div>
        </div>
      </header>
      {loading ? (
        <div className="fcc-card p-12 text-center text-[var(--fcc-muted)]">
          <Loader2 className="w-5 h-5 mx-auto animate-spin" />
          <p className="text-sm mt-3">Composing your family story…</p>
        </div>
      ) : !data ? (
        <div className="fcc-card p-10 text-center text-sm text-[var(--fcc-muted)]">Could not load this month.</div>
      ) : (
        <article className="fcc-card-accent p-7 sm:p-9 space-y-5" data-testid="story-narrative">
          <Sparkles className="w-5 h-5 text-[var(--fcc-green)]" />
          <p className="text-[var(--fcc-ink)] text-lg leading-relaxed whitespace-pre-wrap">{data.narrative}</p>
          <div className="flex flex-wrap gap-2 pt-3 border-t border-[var(--fcc-border)]">
            <span className="fcc-pill bg-white text-[var(--fcc-ink)]">{data.stats.milestones} milestones</span>
            <span className="fcc-pill bg-white text-[var(--fcc-ink)]">{data.stats.emails} emails</span>
            <span className="fcc-pill bg-white text-[var(--fcc-ink)]">{data.stats.events} events</span>
            <span className="fcc-pill bg-white text-[var(--fcc-ink)]">{data.stats.tasks_done} tasks done</span>
          </div>
          {!data.llm_available && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
              AI story unavailable right now — showing raw month summary instead.
            </p>
          )}
          <div className="pt-3 border-t border-[var(--fcc-border)] flex flex-wrap items-center gap-3">
            <FeedbackBar
              surface="family_story"
              targetId={`${year}-${month}-${childId || "all"}`}
            />
            {data.model_used && <AnsweredByPill model_used={data.model_used} className="ml-auto" />}
          </div>
        </article>
      )}
      {shareOpen && (
        <ShareModal
          year={year}
          month={month}
          childId={childId || undefined}
          onClose={() => setShareOpen(false)}
        />
      )}
    </div>
  );
}

function ShareModal({ year, month, childId, onClose }) {
  const [recipients, setRecipients] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const list = recipients.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
    if (list.length === 0) {
      toast.error("Add at least one recipient email.");
      return;
    }
    setSending(true);
    try {
      const r = await shareFamilyStory(year, month, { recipients: list, note }, childId);
      toast.success(
        r?.share?.status === "queued"
          ? "Share queued — recipients will receive the PDF."
          : "Share recorded. Email delivery activates once RESEND_API_KEY is set.",
      );
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't record share.");
    } finally {
      setSending(false);
    }
  };
  return (
    <div
      data-testid="story-share-modal"
      className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl border border-[var(--fcc-border)] w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl text-[var(--fcc-ink)]">Share this Family Story</h3>
          <button
            data-testid="story-share-close"
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-[var(--fcc-cream-2)]"
            aria-label="Close share dialog"
          >
            <X className="w-4 h-4" />
          </button>
        </header>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)]">Recipients</label>
            <input
              data-testid="story-share-recipients"
              type="text"
              placeholder="grandma@family.com, uncle@family.com"
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              className="fcc-input w-full mt-1"
              autoFocus
            />
            <p className="text-[10px] text-[var(--fcc-muted)] mt-1">Separate emails with commas or spaces. Max 10.</p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)]">Personal note (optional)</label>
            <textarea
              data-testid="story-share-note"
              rows={3}
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="A short message to include with the story…"
              className="fcc-input w-full mt-1 resize-none"
            />
          </div>
          <button
            data-testid="story-share-submit"
            type="submit"
            disabled={sending}
            className="w-full fcc-btn-primary text-sm py-2.5 inline-flex items-center justify-center gap-2"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
            {sending ? "Sending…" : "Share story"}
          </button>
          <p className="text-[10px] text-[var(--fcc-muted)] text-center">
            Email delivery activates once RESEND_API_KEY is configured. You can also download the PDF and share it manually.
          </p>
        </form>
      </div>
    </div>
  );
}

export default function FamilyStoryPage() {
  return (
    <PageErrorBoundary scope="family-story">
      <StoryInner />
    </PageErrorBoundary>
  );
}
